import React, { useState, useEffect, useRef } from 'react';
import CompanyManagementAdd from './CompanyManagementAdd';
import { Edit, Trash2, X, ChevronLeft, ChevronRight } from 'lucide-react';
import CompanyDeleteButton from './ManagementButtons/companyDeleteButton';
import { useSelector, useDispatch } from 'react-redux';
import { allcompanylist } from '../../FeatureRedux/CompanyReducer/getCompanylist';
import { addCompany } from '../../FeatureRedux/CompanyReducer/addCompany';
import { updateCompany } from '../../FeatureRedux/CompanyReducer/updateCompany';

const CompanyManageTable = () => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingCompany, setEditingCompany] = useState(null);
  const itemsPerPage = 10;
  const formRef = useRef(null);
  const modalRef = useRef(null);

  const dispatch = useDispatch();
  const componydata = useSelector((s) => s.allcompanylist);

  useEffect(() => {
    dispatch(allcompanylist());
  }, []);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAddForm && formRef.current && !formRef.current.contains(event.target)) {
        setShowAddForm(false);
        setEditingCompany(null);
      }
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShowSuccessModal(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAddForm]);

  const addCompanyData = async (newCompany) => {
    if (editingCompany) {
      await dispatch(updateCompany({ id: editingCompany._id, newCompany }));
    } else {
      await dispatch(addCompany(newCompany));
    }
    dispatch(allcompanylist());
    setShowSuccessModal(true);
    setShowAddForm(false);
  };

  const handleEdit = (id) => {
    const companyToEdit = componydata.data.find((c) => c._id === id);
    setEditingCompany(companyToEdit);
    setShowAddForm(true);
  };

  // Helper functions
  const truncateText = (text, maxLength = 12) => (text ? (text.length > maxLength ? `${text.substring(0, maxLength)}...` : text) : "");
  const formatDate = (dateString) => dateString ? new Date(dateString).toLocaleDateString("en-GB") : "";
  const getDaysLeft = (expiryDate) => {
    if (!expiryDate) return "N/A";
    const daysLeft = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
    return daysLeft > 0 ? `${daysLeft} Days` : "Expired";
  };

  // Pagination
  const totalPages = Math.ceil(componydata.data?.length / itemsPerPage) || 1;
  const currentData = componydata.isSuccess ? componydata.data.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  ) : [];

  return (
    <div className="relative w-full h-[75vh] bg-gray-900 shadow-md rounded-lg border border-gray-700 flex flex-col">
      {/* Header */}
      <div className="p-4 flex justify-between items-center border-b border-gray-700">
        <span className="text-xl font-bold text-white">Company Management List</span>
        <button
          onClick={() => { setEditingCompany(null); setShowAddForm(true); }}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center border border-blue-600"
        >
          <span>Add Company</span>
        </button>
      </div>

      {/* Add/Edit Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div ref={formRef} className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg w-full max-w-2xl p-4 relative">
            <button
              className="absolute -top-3 -right-3 bg-gray-700 w-[30px] h-[30px] border border-gray-600 shadow-md flex items-center justify-center rounded-full text-white hover:bg-gray-600"
              onClick={() => { setShowAddForm(false); setEditingCompany(null); }}
            >
              <X size={16} />
            </button>
            <CompanyManagementAdd
              onClose={() => { setShowAddForm(false); setEditingCompany(null); }}
              addCompanyData={addCompanyData}
              editingCompany={editingCompany}
            />
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div ref={modalRef} className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg p-6 text-center w-full max-w-md">
            <h2 className="text-xl font-semibold text-green-500 mb-4">Success!</h2>
            <p className="text-gray-300 mb-6">Company {editingCompany ? 'updated' : 'added'} successfully.</p>
            <button
              onClick={() => setShowSuccessModal(false)}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors border border-blue-600"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full border-separate border-spacing-0">
          <thead className="bg-gray-900 text-sm font-semibold text-white sticky top-0 z-10">
            <tr>
              {["company_name", "company_email", "company_expiry", "days_left", "password", "permission_location", "action"].map((header) => (
                <th key={header} className="p-3 text-left border-b border-t border-l border-gray-700 first:rounded-tl-lg last:rounded-tr-lg last:border-r">
                  <div className="flex items-center">
                    <span className="uppercase mr-2">{header.replace(/_/g, " ")}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentData.length > 0 ? (
              currentData.map((company) => (
                <tr key={company._id} className="hover:bg-gray-800 cursor-pointer transition-all">
                  {[
                    <span title={company.company_name}>{truncateText(company.company_name)}</span>,
                    <span title={company.company_email}>{truncateText(company.company_email)}</span>,
                    formatDate(company.company_expiry),
                    getDaysLeft(company.company_expiry),
                    <span title={company.password || 'No password set'}>
                      {company.password ? '••••••••' : 'N/A'}
                    </span>,
                    <div>
                      {Array.isArray(company.permission_location) && company.permission_location.length > 0 ? (
                        company.permission_location.map((loc, idx) => {
                          // Handle both city string and cities array formats
                          const cities = loc.city 
                            ? loc.city.split(',').map(c => c.trim())
                            : Array.isArray(loc.cities) 
                              ? loc.cities 
                              : [];
                          return (
                            <div key={idx} className="text-sm">
                              <span title={loc.country || 'N/A'}>{truncateText(loc.country || 'N/A')}</span>
                              {' - '}
                              <span title={loc.state || 'N/A'}>{truncateText(loc.state || 'N/A')}</span>
                              {' : '}
                              <span title={cities.join(', ')}>
                                {`Cities: ${truncateText(cities.join(', '), 10)}`}
                                {cities.length > 4 && (
                                  <span className="text-xs text-gray-400 ml-1">(+{cities.length - 4} more)</span>
                                )}
                              </span>
                            </div>
                          );
                        })
                      ) : (
                        <span className="text-gray-400">No location</span>
                      )}
                    </div>,
                    <div className="flex space-x-2 justify-center">
                      <button onClick={(e) => { e.stopPropagation(); handleEdit(company._id); }} className="text-blue-500 hover:text-blue-700">
                        <Edit className="w-4 h-4" />
                      </button>
                      <CompanyDeleteButton id={company._id} />
                    </div>
                  ].map((value, i) => (
                    <td key={i} className={`p-0 text-sm text-white border-b border-l border-gray-700 ${i === 6 ? "border-r" : ""}`}>
                      <div className="p-3">{value}</div>
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="text-center py-4 text-gray-500 border border-gray-700">
                  {/* {componydata.isSuccess ? "No data available" : "Loading..."} */}
                                {componydata.isSuccess ? ("No data available" ) 
                                : (<span className="flex items-center text-gray-400">Loading...
                  <svg
                    className="ml-2 w-4 h-4 animate-spin text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24" >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                </span>
              )}


                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-between py-4 items-center px-4 bg-gray-900 sticky bottom-0 border-t border-gray-700 rounded-b-lg">
        <button
          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className="px-4 py-2 bg-blue-500 text-white rounded-md flex items-center disabled:opacity-50 border border-blue-600"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Previous
        </button>
        <div className="flex space-x-2">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i + 1)}
              className={`px-4 py-2 rounded-md text-sm font-semibold border ${
                currentPage === i + 1 ? "bg-blue-500 text-white border-blue-600" : "bg-gray-200 text-gray-800 border-gray-300"
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>
        <button
          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages || totalPages === 0}
          className="px-4 py-2 bg-blue-500 text-white rounded-md flex items-center disabled:opacity-50 border border-blue-600"
        >
          Next <ChevronRight className="w-4 h-4 ml-1" />
        </button>
      </div>
    </div>
  );
};

export default CompanyManageTable;


// import React, { useState, useEffect, useRef } from 'react';
// import CompanyManagementAdd from './CompanyManagementAdd';
// import { Edit, Trash2, X, ChevronLeft, ChevronRight } from 'lucide-react';
// import CompanyDeleteButton from './ManagementButtons/companyDeleteButton';
// import { useSelector, useDispatch } from 'react-redux';
// import { allcompanylist } from '../../FeatureRedux/CompanyReducer/getCompanylist';
// import { addCompany } from '../../FeatureRedux/CompanyReducer/addCompany';
// import { updateCompany } from '../../FeatureRedux/CompanyReducer/updateCompany';

// const CompanyManageTable = () => {
//   const [showAddForm, setShowAddForm] = useState(false);
//   const [showSuccessModal, setShowSuccessModal] = useState(false);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [editingCompany, setEditingCompany] = useState(null);
//   const itemsPerPage = 10;
//   const formRef = useRef(null);
//   const modalRef = useRef(null);

//   const dispatch = useDispatch();
//   const componydata = useSelector((s) => s.allcompanylist);

//   useEffect(() => {
//     dispatch(allcompanylist());
//   }, []);

//   // Handle click outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (showAddForm && formRef.current && !formRef.current.contains(event.target)) {
//         setShowAddForm(false);
//         setEditingCompany(null);
//       }
//       if (modalRef.current && !modalRef.current.contains(event.target)) {
//         setShowSuccessModal(false);
//       }
//     };
//     document.addEventListener('mousedown', handleClickOutside);
//     return () => document.removeEventListener('mousedown', handleClickOutside);
//   }, [showAddForm]);

//   const addCompanyData = async (newCompany) => {
//     if (editingCompany) {
//       await dispatch(updateCompany({ id: editingCompany._id, newCompany }));
//     } else {
//       await dispatch(addCompany(newCompany));
//     }
//     dispatch(allcompanylist());
//     setShowSuccessModal(true);
//     setShowAddForm(false);
//   };

//   const handleEdit = (id) => {
//     const companyToEdit = componydata.data.find((c) => c._id === id);
//     setEditingCompany(companyToEdit);
//     setShowAddForm(true);
//   };

//   // Helper functions
//   const truncateText = (text, maxLength = 12) => (text ? (text.length > maxLength ? `${text.substring(0, maxLength)}...` : text) : "");
//   const formatDate = (dateString) => dateString ? new Date(dateString).toLocaleDateString("en-GB") : "";
//   const getDaysLeft = (expiryDate) => {
//     if (!expiryDate) return "N/A";
//     const daysLeft = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
//     return daysLeft > 0 ? `${daysLeft} Days` : "Expired";
//   };

//   // Pagination
//   const totalPages = Math.ceil(componydata.data?.length / itemsPerPage) || 1;
//   const currentData = componydata.isSuccess ? componydata.data.slice(
//     (currentPage - 1) * itemsPerPage,
//     currentPage * itemsPerPage
//   ) : [];

//   return (
//     <div className="relative w-full h-[75vh] bg-gray-900 shadow-md rounded-lg border border-gray-700 flex flex-col">
//       {/* Header */}
//       <div className="p-4 flex justify-between items-center border-b border-gray-700">
//         <span className="text-xl font-bold text-white">Company Management List</span>
//         <button
//           onClick={() => { setEditingCompany(null); setShowAddForm(true); }}
//           className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center border border-blue-600"
//         >
//           <span>Add Company</span>
//         </button>
//       </div>

//       {/* Add/Edit Form Modal */}
//       {showAddForm && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div ref={formRef} className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg w-full max-w-2xl p-4 relative">
//             <button
//               className="absolute -top-3 -right-3 bg-gray-700 w-[30px] h-[30px] border border-gray-600 shadow-md flex items-center justify-center rounded-full text-white hover:bg-gray-600"
//               onClick={() => { setShowAddForm(false); setEditingCompany(null); }}
//             >
//               <X size={16} />
//             </button>
//             <CompanyManagementAdd
//               onClose={() => { setShowAddForm(false); setEditingCompany(null); }}
//               addCompanyData={addCompanyData}
//               editingCompany={editingCompany}
//             />
//           </div>
//         </div>
//       )}

//       {/* Success Modal */}
//       {showSuccessModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div ref={modalRef} className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg p-6 text-center w-full max-w-md">
//             <h2 className="text-xl font-semibold text-green-500 mb-4">Success!</h2>
//             <p className="text-gray-300 mb-6">Company {editingCompany ? 'updated' : 'added'} successfully.</p>
//             <button
//               onClick={() => setShowSuccessModal(false)}
//               className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors border border-blue-600"
//             >
//               Close
//             </button>
//           </div>
//         </div>
//       )}

//       {/* Table */}
//       <div className="flex-1 overflow-auto">
//         <table className="w-full border-separate border-spacing-0">
//           <thead className="bg-gray-900 text-sm font-semibold text-white sticky top-0 z-10">
//             <tr>
//               {["company_name", "company_email", "company_expiry", "days_left", "password", "permission_location", "action"].map((header) => (
//                 <th key={header} className="p-3 text-left border-b border-t border-l border-gray-700 first:rounded-tl-lg last:rounded-tr-lg last:border-r">
//                   <div className="flex items-center">
//                     <span className="uppercase mr-2">{header.replace(/_/g, " ")}</span>
//                   </div>
//                 </th>
//               ))}
//             </tr>
//           </thead>
//           <tbody>
//             {currentData.length > 0 ? (
//               currentData.map((company) => (
//                 <tr key={company._id} className="hover:bg-gray-800 cursor-pointer transition-all">
//                   {[
//                     <span title={company.company_name}>{truncateText(company.company_name)}</span>,
//                     <span title={company.company_email}>{truncateText(company.company_email)}</span>,
//                     formatDate(company.company_expiry),
//                     getDaysLeft(company.company_expiry),
//                     <span title={company.password || 'No password set'}>
//                       {company.password ? '••••••••' : 'N/A'}
//                     </span>,
//                     <div>
//                       {/* {console.log(`This is permission location ${JSON.stringify(company.permission_location)}`)} */}
//                       {Array.isArray(company.permission_location) && company.permission_location.length > 0 ? (
//                         company.permission_location.map((loc, idx) => (
//                           <div key={idx} className="text-sm">
//                             <span title={loc.country || 'N/A'}>{truncateText(loc.country || 'N/A')}</span>
//                             {' - '}
//                             <span title={loc.state || 'N/A'}>{truncateText(loc.state || 'N/A')}</span>
//                             {' : '}
//                             <span
//                               title={
//                                 Array.isArray(loc?.city)
//                                   ? loc.city.join(', ')
//                                   : typeof loc?.city === 'string' && loc.city.trim() !== ''
//                                   ? loc.city
//                                   : 'N/A'
//                               }
//                             >
//                               {`Cities: ${truncateText(
//                                 Array.isArray(loc?.city) ? loc.city.join(', ') : loc?.city,
//                                 10
//                               )}`}
//                             </span>

//                           </div>
//                         ))
//                       ) : (
//                         <span className="text-gray-400">No location</span>
//                       )}
//                     </div>,
//                     <div className="flex space-x-2 justify-center">
//                       <button onClick={(e) => { e.stopPropagation(); handleEdit(company._id); }} className="text-blue-500 hover:text-blue-700">
//                         <Edit className="w-4 h-4" />
//                       </button>
//                       <CompanyDeleteButton id={company._id} />
//                     </div>
//                   ].map((value, i) => (
//                     <td key={i} className={`p-0 text-sm text-white border-b border-l border-gray-700 ${i === 6 ? "border-r" : ""}`}>
//                       <div className="p-3">{value}</div>
//                     </td>
//                   ))}
//                 </tr>
//               ))
//             ) : (
//               <tr>
//                 <td colSpan={7} className="text-center py-4 text-gray-500 border border-gray-700">
//                   {/* {componydata.isSuccess ? "No data available" : "Loading..."} */}
//                                 {componydata.isSuccess ? ("No data available" ) 
//                                 : (<span className="flex items-center text-gray-400">Loading...
//                   <svg
//                     className="ml-2 w-4 h-4 animate-spin text-gray-400"
//                     xmlns="http://www.w3.org/2000/svg"
//                     fill="none"
//                     viewBox="0 0 24 24" >
//                     <circle
//                       className="opacity-25"
//                       cx="12"
//                       cy="12"
//                       r="10"
//                       stroke="currentColor"
//                       strokeWidth="4"
//                     />
//                     <path
//                       className="opacity-75"
//                       fill="currentColor"
//                       d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
//                     />
//                   </svg>
//                 </span>
//               )}


//                 </td>
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>

//       {/* Pagination */}
//       <div className="flex justify-between py-4 items-center px-4 bg-gray-900 sticky bottom-0 border-t border-gray-700 rounded-b-lg">
//         <button
//           onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
//           disabled={currentPage === 1}
//           className="px-4 py-2 bg-blue-500 text-white rounded-md flex items-center disabled:opacity-50 border border-blue-600"
//         >
//           <ChevronLeft className="w-4 h-4 mr-1" /> Previous
//         </button>
//         <div className="flex space-x-2">
//           {Array.from({ length: totalPages }, (_, i) => (
//             <button
//               key={i}
//               onClick={() => setCurrentPage(i + 1)}
//               className={`px-4 py-2 rounded-md text-sm font-semibold border ${
//                 currentPage === i + 1 ? "bg-blue-500 text-white border-blue-600" : "bg-gray-200 text-gray-800 border-gray-300"
//               }`}
//             >
//               {i + 1}
//             </button>
//           ))}
//         </div>
//         <button
//           onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
//           disabled={currentPage === totalPages || totalPages === 0}
//           className="px-4 py-2 bg-blue-500 text-white rounded-md flex items-center disabled:opacity-50 border border-blue-600"
//         >
//           Next <ChevronRight className="w-4 h-4 ml-1" />
//         </button>
//       </div>
//     </div>
//   );
// };

// export default CompanyManageTable;
// above code is also working .... 
// import React, { useState, useEffect, useRef } from 'react';
// import CompanyManagementAdd from './CompanyManagementAdd';
// import { Edit, Trash2, X, ChevronLeft, ChevronRight } from 'lucide-react';
// import CompanyDeleteButton from './ManagementButtons/companyDeleteButton';
// import { useSelector, useDispatch } from 'react-redux';
// import { allcompanylist } from '../../FeatureRedux/CompanyReducer/getCompanylist';
// import { addCompany } from '../../FeatureRedux/CompanyReducer/addCompany';
// import { updateCompany } from '../../FeatureRedux/CompanyReducer/updateCompany';

// const CompanyManageTable = () => {
//   const [showAddForm, setShowAddForm] = useState(false);
//   const [companyData, setCompanyData] = useState([]);
//   const [showSuccessModal, setShowSuccessModal] = useState(false);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [editingCompany, setEditingCompany] = useState(null);
//   const itemsPerPage = 2;
//   const formRef = useRef(null);
//   const modalRef = useRef(null);

//   const dispatch = useDispatch();
//   const componydata = useSelector((s) => s.allcompanylist);

//   useEffect(() => {
//     dispatch(allcompanylist());
//     console.log("api dispatch called ");
//   }, []);

//   // Handle click outside the form
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (showAddForm && formRef.current && !formRef.current.contains(event.target)) {
//         setShowAddForm(false);
//         setEditingCompany(null);
//       }
//       if (modalRef.current && !modalRef.current.contains(event.target)) {
//         setShowSuccessModal(false);
//       }
//     };

//     document.addEventListener('mousedown', handleClickOutside);
//     return () => {
//       document.removeEventListener('mousedown', handleClickOutside);
//     };
//   }, [showAddForm]);

//   const addCompanyData = async (newCompany) => {
//     if (editingCompany != null) {
//       console.log("edit clicked");
//       await dispatch(updateCompany({ id: editingCompany._id, newCompany }));
//       dispatch(allcompanylist());
//       return;
//     }
    
//     await dispatch(addCompany(newCompany));
//     dispatch(allcompanylist());
//     setShowSuccessModal(true);
//   };

//   const handleEdit = (id) => {
//     const companyToEdit = componydata.data.find((company) => company._id === id);
//     setEditingCompany(companyToEdit);
//     setShowAddForm(true);
//   };

//   // Truncate long text to 12 characters
//   const truncateText = (text) => {
//     if (!text) return "";
//     return text.length > 12 ? `${text.substring(0, 12)}...` : text;
//   };

//   // Format date
//   const formatDate = (dateString) => {
//     if (!dateString) return "";
//     const date = new Date(dateString);
//     return date.toLocaleDateString("en-GB");
//   };

//   // Pagination logic
//   const totalPages = Math.ceil(componydata.data?.length / itemsPerPage) || 1;
//   const currentData = componydata.isSuccess 
//     ? componydata.data.slice(
//         (currentPage - 1) * itemsPerPage,
//         currentPage * itemsPerPage
//       )
//     : [];

//   const handlePageChange = (page) => {
//     setCurrentPage(page);
//   };


//   const getDaysLeft = (expiryDate) => {
//     const expiry = new Date(expiryDate);
//     const today = new Date();
//     const timeDiff = expiry - today;
//     const daysLeft = Math.ceil(timeDiff / (1000 * 60 * 60 * 24)); // milliseconds to days
  
//     return daysLeft > 0 ? `${daysLeft} Days` : "Expired";
//   };
  

//   return (
//     <div className="relative w-full h-[75vh] bg-gray-900 shadow-md rounded-lg border border-gray-700 flex flex-col">
//       <div className="p-4 flex justify-between items-center border-b border-gray-700">
//         <span className="text-xl font-bold text-white">Company Management List</span>
//         <button
//           onClick={() => {
//             setEditingCompany(null);
//             setShowAddForm(true);
//           }}
//           className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center border border-blue-600"
//         >
//           <span>Add Company</span>
//         </button>
//       </div>

//       {/* Modal for Add Company Form */}
//       {showAddForm && (
        
//             <CompanyManagementAdd
//               onClose={() => {
//                 setShowAddForm(false);
//                 setEditingCompany(null);
//               }}
//               addCompanyData={addCompanyData}
//               editingCompany={editingCompany}
//             />
      
//       )}

//       {/* Success Modal */}
//       {showSuccessModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div 
//             ref={modalRef}
//             className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg p-6 text-center w-full max-w-md"
//           >
//             <h2 className="text-xl font-semibold text-green-500 mb-4">Success!</h2>
//             <p className="text-gray-300 mb-6">Company data has been successfully updated.</p>
//             <button
//               onClick={() => setShowSuccessModal(false)}
//               className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors border border-blue-600"
//             >
//               Close
//             </button>
//           </div>
//         </div>
//       )}

//       {/* Table */}
//       <div className="flex-1 overflow-auto">
//         <table className="w-full border-separate border-spacing-0">
//           <thead className="bg-gray-900 text-sm font-semibold text-white sticky top-0 z-10">
//             <tr>
//               {[
//                 "company_name",
//                 "company_email",
//                 "company_expiry",
//                 "days_left",
//                 "password",
//                 "permission_location",
//                 "action",
//               ].map((header) => (
//                 <th 
//                   key={header} 
//                   className="p-3 text-left border-b border-t border-l border-gray-700 first:rounded-tl-lg last:rounded-tr-lg last:border-r"
//                 >
//                   <div className="flex items-center">
//                     <span className="uppercase mr-2">{header.replace(/_/g, " ")}</span>
//                   </div>
//                 </th>
//               ))}
//             </tr>
//           </thead>
//           <tbody>
//             {currentData.length > 0 ? (
//               currentData.map((company, rowIndex) => (
//                 <tr
//                   key={company._id}
//                   className="hover:bg-gray-800 cursor-pointer transition-all"
//                 >
//                   {[
//                     <span title={company.company_name} className="block py-2">
//                       {truncateText(company.company_name)}
//                     </span>,
//                     <span title={company.company_email}>
//                       {truncateText(company.company_email)}
//                     </span>,
//                     formatDate(company.company_expiry),

//                     <span title={company.company_expiry}>
//                       {truncateText(getDaysLeft(company.company_expiry))  } 
//                     </span>,
//                     <span title={company.password || 'N/A'}>
//                       {truncateText(company.password || 'N/A')}
//                     </span>,
//                     <div>
//                       {Array.isArray(company.permission_location) ? (
//                         company.permission_location
//                           .filter((location) => Array.isArray(location.cities) && location.cities.length > 0)
//                           .map((location, idx) => (
//                             <div key={idx} className="text-sm">
//                               <span title={location.country} className="font-medium">
//                                 {truncateText(location.country)}
//                               </span>
//                               {' - '}
//                               <span title={location.state}>
//                                 {truncateText(location.state)}
//                               </span>
//                               {' : '}
//                               <span title={location.cities.join(', ')}>
//                                 {truncateText(location.cities.join(', '))}
//                               </span>
//                             </div>
//                           ))
//                       ) : (
//                         <div>No locations available</div>
//                       )}
//                     </div>,
//                     <div className="flex space-x-2 items-center justify-center">
//                       <button
//                         onClick={(e) => {
//                           e.stopPropagation();
//                           handleEdit(company._id);
//                         }}
//                         className="text-blue-500 hover:text-blue-700"
//                       >
//                         <Edit className="w-4 h-4" />
//                       </button>
//                       <div className='pt-1'>
//                       <CompanyDeleteButton id={company._id} />
//                       </div>
//                     </div>,
//                   ].map((value, colIndex) => (
//                     <td 
//                       key={colIndex} 
//                       className={`p-0 text-sm text-white border-b border-l border-gray-700 
//                         ${colIndex === 0 ? "border-l-0" : ""}
//                         ${colIndex === 5 ? "border-r border-r-gray-700" : ""}
//                         ${rowIndex === currentData.length - 1 ? "border-b" : ""}
//                         ${rowIndex === currentData.length - 1 && colIndex === 0 ? "rounded-bl-lg" : ""}
//                         ${rowIndex === currentData.length - 1 && colIndex === 5 ? "rounded-br-lg" : ""}
//                       `}
//                     >
//                       <div className="p-3">
//                         {value}
//                       </div>
//                     </td>
//                   ))}
//                 </tr>
//               ))
//             ) : (
//               <tr>
//                 <td 
//                   colSpan={6} 
//                   className="text-center py-4 text-gray-500 border border-gray-700"
//                 >
//                   {componydata.isSuccess ? "No data available" : "Loading..."}
//                 </td>
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>

//       {/* Pagination */}
//       <div className="flex justify-between py-4 items-center px-4 bg-gray-900 sticky bottom-0 border-t border-gray-700 rounded-b-lg">
//         <button
//           onClick={() => handlePageChange(currentPage - 1)}
//           className="mx-2 px-4 py-2 rounded-md bg-blue-500 text-white flex items-center disabled:opacity-50 border border-blue-600"
//           disabled={currentPage === 1}
//         >
//           <ChevronLeft className="w-4 h-4 mr-1" /> Previous
//         </button>

//         <div className="flex space-x-2">
//           {Array.from({ length: totalPages }, (_, index) => (
//             <button
//               key={index}
//               onClick={() => handlePageChange(index + 1)}
//               className={`px-4 py-2 rounded-md text-sm font-semibold transition-all border ${
//                 currentPage === index + 1 
//                   ? "bg-blue-500 text-white border-blue-600" 
//                   : "bg-gray-200 text-gray-800 border-gray-300"
//               }`}
//             >
//               {index + 1}
//             </button>
//           ))}
//         </div>

//         <button
//           onClick={() => handlePageChange(currentPage + 1)}
//           className="mx-2 px-4 py-2 rounded-md bg-blue-500 text-white flex items-center disabled:opacity-50 border border-blue-600"
//           disabled={currentPage === totalPages || totalPages === 0}
//         >
//           Next <ChevronRight className="w-4 h-4 ml-1" />
//         </button>
//       </div>
//     </div>
//   );
// };

// export default CompanyManageTable;


// import React, { useState, useEffect } from 'react';
// import CompanyManagementAdd from './CompanyManagementAdd'; // Import the CompanyManagementAdd component
// import { Edit, Trash, X } from 'lucide-react'; // Icons for edit and delete
// import CompanyDeleteButton from './ManagementButtons/companyDeleteButton';
// import { useSelector, useDispatch } from 'react-redux';
// import { allcompanylist } from '../../FeatureRedux/CompanyReducer/getCompanylist'
// import {addCompany} from '../../FeatureRedux/CompanyReducer/addCompany'
// import {updateCompany } from '../../FeatureRedux/CompanyReducer/updateCompany'

// const CompanyManageTable = () => {
//   const [showAddForm, setShowAddForm] = useState(false); // State to toggle the form visibility
//   const [companyData, setCompanyData] = useState([]); // State to manage company data
//   const [showSuccessModal, setShowSuccessModal] = useState(false); // State to toggle success modal
//   const [currentPage, setCurrentPage] = useState(1); // State for pagination
//   const [editingCompany, setEditingCompany] = useState(null); // State to track the company being edited
//   const entriesPerPage = 5; // Number of entries per page



//   const dispatch = useDispatch();

//   const componydata = useSelector((s) => s.allcompanylist);
//   console.log("This is Company data", componydata);




//   useEffect(() => {
//     dispatch(allcompanylist())
//     console.log("api dispatchcalled ")
//     // const storedCompanyData = JSON.parse(localStorage.getItem('companyData')) || [];
//     // setCompanyData(storedCompanyData);
//   }, []);

//   // Add or update company data
//   const addCompanyData =async (newCompany) => {

//     if(editingCompany != null ){
//       console.log("edit clicked");
//       // console.log("This is company data",editingCompany._id , newCompany)
//       await dispatch(updateCompany({ id: editingCompany._id, newCompany }))
//       dispatch(allcompanylist())
//       return ;
//     }
     
//     // console.log("This is company data",newCompany)
//     await dispatch(addCompany(newCompany)) 
//     dispatch(allcompanylist()) 
//     // setCompanyData(updatedCompanyData);
//     // localStorage.setItem('companyData', JSON.stringify(updatedCompanyData));
//     setShowSuccessModal(true); // Show success modal
//   };

//   // Handle edit action
//   const handleEdit = (id) => {
//     const companyToEdit = componydata.data.find((company) => company._id === id);
//     setEditingCompany(companyToEdit); // Set the company being edited
    
    

//     setShowAddForm(true); 

//   };

//   // console.log(`editingCompany data ${JSON.stringify(editingCompany)}`)


//   // Pagination logic
//   const indexOfLastEntry = currentPage * entriesPerPage;
//   const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
//   const currentEntries =  componydata.isSuccess ? componydata.data.slice(indexOfFirstEntry, indexOfLastEntry) : [];

//   // Change page
//   const paginate = (pageNumber) => setCurrentPage(pageNumber);

//   return (
//     <div className="space-y-6">
//       <div className="flex justify-between items-center">
//       <span className="text-2xl font-bold text-gray-800">Company Management List</span>

//         <button
//           onClick={() => {
//             setEditingCompany(null); // Reset editing company
//             setShowAddForm(true);
//           }}
//           className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center"
//         >
//           <span>Add Company Management</span>
//         </button>
//       </div>

//       {/* Modal for Add Company Form */}
//       {showAddForm && (
//         <div className="fixed inset-0 flex items-center justify-center z-50">
//           <div className="bg-white rounded-lg shadow-lg w-10/12 md:w-3/4 lg:w-1/2 p-6 relative">
//             <button
//               className="
//                 absolute 
//                 -top-3 
//                 -right-3 
//                 bg-white 
//                 w-[30px] 
//                 h-[30px] 
//                 border 
//                 border-gray-300 
//                 shadow-md 
//                 flex 
//                 items-center 
//                 justify-center
//                 rounded-full
//               "
//               onClick={() => {
//                 setShowAddForm(false);
//                 setEditingCompany(null); // Reset editing company
//               }}
//             >
//               <X size={16} />
//             </button>
//             <CompanyManagementAdd
//               onClose={() => {
//                 setShowAddForm(false);
//                 setEditingCompany(null); // Reset editing company
//               }}
//               addCompanyData={addCompanyData}
//               editingCompany={editingCompany} // Pass the company being edited
//             />
//           </div>
//         </div>
//       )}

//       {/* Success Modal */}
//       {showSuccessModal && (
//         <div className="fixed inset-0 flex items-center justify-center z-50">
//           <div className="bg-white rounded-lg shadow-lg p-6 text-center">
//             <h2 className="text-2xl font-bold text-green-600 mb-4">Success!</h2>
//             <p className="text-gray-700">Company data has been successfully updated.</p>
//             <button
//               onClick={() => setShowSuccessModal(false)}
//               className="mt-4 px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
//             >
//               Close
//             </button>
//           </div>
//         </div>
//       )}

//       {/* Table */}
//       <div className="bg-white rounded-lg shadow-lg overflow-hidden">
//         <table className="w-full">
//           <thead className="bg-gray-200">
//             <tr>
//               <th className="p-3 text-left">Company Name</th>
//               <th className="p-3 text-left">Email</th>
//               <th className="p-3 text-left">Expiry Date</th>
//               <th className="p-3 text-left">Password</th>
//               <th className="p-3 text-left">Locations</th>
//               <th className="p-3 text-left">Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {componydata.isSuccess &&  componydata.data.length > 0 ? (
//               componydata.data.map((company, index) => (
                
//                 <tr key={company._id} className="border-b border-gray-200 hover:bg-gray-50">
//                   <td className="p-3 border">{company.company_name}</td>
//                   <td className="p-3 border">{company.company_email}</td>
//                   <td className="p-3 border">{company.company_expiry}</td>
//                   <td className="p-3 border">{company.password || 'N/A'}</td>
//                   <td className="p-3 border">
//                     { Array.isArray(componydata.data) && Array.isArray(company.permission_location) ? (
//                       company.permission_location
//                         .filter((location) => Array.isArray(location.cities) && location.cities.length > 0) // Filter out empty entries
//                         .map((location, idx) => (
//                           <div key={idx}>
//                             <strong>{location.country}</strong> - {location.state} :{' '}
//                             {location.cities.join(', ')}
//                           </div>
//                         ))
//                     ) : (
//                       <div>No locations available</div>
//                     )}
//                   </td>
//                   <td className="p-3 flex space-x-2">
//                     <button
//                       onClick={() => handleEdit(company._id)}
//                       className="text-blue-500 hover:text-blue-600"
//                     >
//                       <Edit size={16} />
//                     </button>
//                     <CompanyDeleteButton id={company._id}  />
//                   </td>
//                 </tr>
//               ))
//             ) : (
//               <tr>
//                 <td colSpan="6" className="text-center p-3">No data available</td>
//               </tr>
//             )}

//           </tbody>
//         </table>
//       </div>

//       {/* Pagination */}
//       <div className="flex justify-center mt-4">
//         {Array.from({ length: Math.ceil(componydata.length / entriesPerPage) }, (_, i) => (
//           <button
//             key={i + 1}
//             onClick={() => paginate(i + 1)}
//             className={`px-4 py-2 mx-1 rounded-md ${currentPage === i + 1
//                 ? 'bg-blue-500 text-white'
//                 : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
//               }`}
//           >
//             {i + 1}
//           </button>
//         ))}
//       </div>
//     </div>
//   );
// };

// export default CompanyManageTable;


// import React, { useState, useEffect, useRef } from 'react';
// import CompanyManagementAdd from './CompanyManagementAdd';
// import { Edit, Trash, X } from 'lucide-react';
// import CompanyDeleteButton from './ManagementButtons/companyDeleteButton';
// import { useSelector, useDispatch } from 'react-redux';
// import { allcompanylist } from '../../FeatureRedux/CompanyReducer/getCompanylist';
// import { addCompany } from '../../FeatureRedux/CompanyReducer/addCompany';
// import { updateCompany } from '../../FeatureRedux/CompanyReducer/updateCompany';

// const CompanyManageTable = () => {
//   const [showAddForm, setShowAddForm] = useState(false);
//   const [companyData, setCompanyData] = useState([]);
//   const [showSuccessModal, setShowSuccessModal] = useState(false);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [editingCompany, setEditingCompany] = useState(null);
//   const entriesPerPage = 5;
//   const formRef = useRef(null);

//   const dispatch = useDispatch();
//   const componydata = useSelector((s) => s.allcompanylist);

//   useEffect(() => {
//     dispatch(allcompanylist());
//     console.log("api dispatch called ");
//   }, []);

//   // Handle click outside the form
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (showAddForm && formRef.current && !formRef.current.contains(event.target)) {
//         setShowAddForm(false);
//         setEditingCompany(null);
//       }
//     };

//     if (showAddForm) {
//       document.addEventListener('mousedown', handleClickOutside);
//     }

//     return () => {
//       document.removeEventListener('mousedown', handleClickOutside);
//     };
//   }, [showAddForm]);

//   const addCompanyData = async (newCompany) => {
//     if (editingCompany != null) {
//       console.log("edit clicked");
//       await dispatch(updateCompany({ id: editingCompany._id, newCompany }));
//       dispatch(allcompanylist());
//       return;
//     }
    
//     await dispatch(addCompany(newCompany));
//     dispatch(allcompanylist());
//     setShowSuccessModal(true);
//   };

//   const handleEdit = (id) => {
//     const companyToEdit = componydata.data.find((company) => company._id === id);
//     setEditingCompany(companyToEdit);
//     setShowAddForm(true);
//   };

//   const indexOfLastEntry = currentPage * entriesPerPage;
//   const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
//   const currentEntries = componydata.isSuccess ? componydata.data.slice(indexOfFirstEntry, indexOfLastEntry) : [];

//   const paginate = (pageNumber) => setCurrentPage(pageNumber);

//   return (
//     <div className="space-y-6">
//       <div className="flex justify-between items-center">
//         <span className="text-2xl font-bold text-white">Company Management List</span>
//         <button
//           onClick={() => {
//             setEditingCompany(null);
//             setShowAddForm(true);
//           }}
//           className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center"
//         >
//           <span>Add Company Management</span>
//         </button>
//       </div>

//       {/* Modal for Add Company Form */}
//       {showAddForm && (
//         <div className="fixed inset-0 flex items-center justify-center z-50 bg-opacity-50">
//           <div 
//             ref={formRef}
//             className="bg-white rounded-lg shadow-lg w-10/12 md:w-3/4 lg:w-1/2 p-6 relative"
//           >
//             <button
//               className="
//                 absolute 
//                 -top-3 
//                 -right-3 
//                 bg-white 
//                 w-[30px] 
//                 h-[30px] 
//                 border 
//                 border-gray-300 
//                 shadow-md 
//                 flex 
//                 items-center 
//                 justify-center
//                 rounded-full
//               "
//               onClick={() => {
//                 setShowAddForm(false);
//                 setEditingCompany(null);
//               }}
//             >
//               <X size={16} />
//             </button>
//             <CompanyManagementAdd
//               onClose={() => {
//                 setShowAddForm(false);
//                 setEditingCompany(null);
//               }}
//               addCompanyData={addCompanyData}
//               editingCompany={editingCompany}
//             />
//           </div>
//         </div>
//       )}

//       {/* Success Modal */}
//       {showSuccessModal && (
//         <div className="fixed inset-0 flex items-center justify-center z-50">
//           <div className="bg-white rounded-lg shadow-lg p-6 text-center">
//             <h2 className="text-2xl font-bold text-green-600 mb-4">Success!</h2>
//             <p className="text-gray-700">Company data has been successfully updated.</p>
//             <button
//               onClick={() => setShowSuccessModal(false)}
//               className="mt-4 px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
//             >
//               Close
//             </button>
//           </div>
//         </div>
//       )}

//       {/* Table */}
//       <div className="bg-white rounded-lg shadow-lg overflow-hidden">
//         <table className="w-full">
//           <thead className="bg-gray-200">
//             <tr>
//               <th className="p-3 text-left">Company Name</th>
//               <th className="p-3 text-left">Email</th>
//               <th className="p-3 text-left">Expiry Date</th>
//               <th className="p-3 text-left">Password</th>
//               <th className="p-3 text-left">Locations</th>
//               <th className="p-3 text-left">Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {componydata.isSuccess && componydata.data.length > 0 ? (
//               componydata.data.map((company, index) => (
//                 <tr key={company._id} className="border-b border-gray-200 hover:bg-gray-50">
//                   <td className="p-3 border">{company.company_name}</td>
//                   <td className="p-3 border">{company.company_email}</td>
//                   <td className="p-3 border">{company.company_expiry}</td>
//                   <td className="p-3 border">{company.password || 'N/A'}</td>
//                   <td className="p-3 border">
//                     {Array.isArray(componydata.data) && Array.isArray(company.permission_location) ? (
//                       company.permission_location
//                         .filter((location) => Array.isArray(location.cities) && location.cities.length > 0)
//                         .map((location, idx) => (
//                           <div key={idx}>
//                             <strong>{location.country}</strong> - {location.state} :{' '}
//                             {location.cities.join(', ')}
//                           </div>
//                         ))
//                     ) : (
//                       <div>No locations available</div>
//                     )}
//                   </td>
//                   <td className="p-3 flex space-x-2">
//                     <button
//                       onClick={() => handleEdit(company._id)}
//                       className="text-blue-500 hover:text-blue-600"
//                     >
//                       <Edit size={16} />
//                     </button>
//                     <CompanyDeleteButton id={company._id} />
//                   </td>
//                 </tr>
//               ))
//             ) : (
//               <tr>
//                 <td colSpan="6" className="text-center p-3">No data available</td>
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>

//       {/* Pagination */}
//       <div className="flex justify-center mt-4">
//         {Array.from({ length: Math.ceil(componydata.length / entriesPerPage) }, (_, i) => (
//           <button
//             key={i + 1}
//             onClick={() => paginate(i + 1)}
//             className={`px-4 py-2 mx-1 rounded-md ${
//               currentPage === i + 1
//                 ? 'bg-blue-500 text-white'
//                 : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
//             }`}
//           >
//             {i + 1}
//           </button>
//         ))}
//       </div>
//     </div>
//   );
// };

// export default CompanyManageTable;





